// ---------------------------------------------------------------------------
// DnD Email Editor – Default Values & Factory Functions
// ---------------------------------------------------------------------------

import type {
  Spacing,
  BorderSide,
  BorderSides,
  GlobalStyles,
  WrapperSettings,
  DndEmailModel,
  LayerBase,
  TextLayer,
  ImageLayer,
  ButtonLayer,
  DividerLayer,
  SpacerLayer,
  HtmlLayer,
  ColumnsLayer,
  SocialLayer,
  SocialLink,
  SocialLogos,
  SocialPlatform,
  VideoLayer,
  CountdownLayer,
  Column,
  TextElement,
  ImageElement,
  ButtonElement,
  DividerElement,
  SpacerElement,
  HtmlElement,
  SocialElement,
  Layer,
  Element,
  LayerType,
  ElementType,
} from './dnd-model';
import { generateId, CURRENT_EDITOR_VERSION } from './dnd-model';
import { LAYOUT_COLUMN_COUNTS } from './dnd-columns';

// ---- Primitive defaults ---------------------------------------------------

export function defaultSpacing(all = 0): Spacing {
  return { top: all, right: all, bottom: all, left: all };
}

export function defaultBorderSide(): BorderSide {
  return { width: 0, style: 'none', color: '#000000' };
}

export function defaultBorderSides(): BorderSides {
  return {
    top: defaultBorderSide(),
    right: defaultBorderSide(),
    bottom: defaultBorderSide(),
    left: defaultBorderSide(),
  };
}

// ---- Global defaults ------------------------------------------------------

export function defaultGlobalStyles(): GlobalStyles {
  return {
    bodyBgColor: '',
    contentBgColor: '#ffffff',
    fontFamily: 'Arial, Helvetica, sans-serif',
    fontSize: '16px',
    textColor: '#333333',
    lineHeight: '1.5',
    linkColor: '#1a73e8',
    linkUnderline: true,
    headingFontFamily: 'Arial, Helvetica, sans-serif',
    h1: { fontSize: '28px', color: '#222222', fontWeight: 'bold' },
    h2: { fontSize: '22px', color: '#222222', fontWeight: 'bold' },
    h3: { fontSize: '18px', color: '#222222', fontWeight: 'bold' },
    defaultButtonStyle: {
      bgColor: '#1a73e8',
      textColor: '#ffffff',
      borderRadius: '4px',
      fontFamily: 'Arial, Helvetica, sans-serif',
      fontSize: '16px',
      fontWeight: 'bold',
    },
  };
}

export function defaultWrapperSettings(): WrapperSettings {
  return {
    maxWidth: 600,
    padding: defaultSpacing(0),
    bgColor: '#ffffff',
  };
}

// ---- LayerBase defaults ---------------------------------------------------

function layerBaseDefaults(label: string): Omit<LayerBase, 'id'> {
  return {
    label,
    locked: true,
    bgColor: '',
    textColor: '',
    bgImage: '',
    bgRepeat: 'no-repeat',
    bgPosition: 'center center',
    bgSize: 'cover',
    contentBgColor: '',
    padding: defaultSpacing(10),
    border: defaultBorderSides(),
    margin: defaultSpacing(0),
    hideOnMobile: false,
    hideOnDesktop: false,
  };
}

// ---- Layer factory functions ----------------------------------------------

export function createTextLayer(overrides?: Partial<TextLayer>): TextLayer {
  return {
    ...layerBaseDefaults('Text'),
    id: generateId('layer'),
    type: 'text',
    content: '<p>Enter your text here</p>',
    textAlign: 'left',
    lineHeight: '',
    ...overrides,
  };
}

export function createImageLayer(overrides?: Partial<ImageLayer>): ImageLayer {
  return {
    ...layerBaseDefaults('Image'),
    id: generateId('layer'),
    type: 'image',
    padding: defaultSpacing(0),
    src: '',
    alt: '',
    width: 600,
    autoWidth: true,
    href: '',
    target: '_blank',
    align: 'center',
    borderRadius: 0,
    retina: false,
    ...overrides,
  };
}

export function createButtonLayer(
  overrides?: Partial<ButtonLayer>,
): ButtonLayer {
  return {
    ...layerBaseDefaults('Button'),
    id: generateId('layer'),
    type: 'button',
    text: 'Click Here',
    href: '#',
    target: '_blank',
    buttonBgColor: '#1a73e8',
    textColor: '#ffffff',
    fontFamily: 'Arial, Helvetica, sans-serif',
    fontSize: '16px',
    fontWeight: 'bold',
    borderRadius: 4,
    buttonPadding: { top: 12, right: 24, bottom: 12, left: 24 },
    fullWidth: false,
    align: 'center',
    ...overrides,
  };
}

export function createDividerLayer(
  overrides?: Partial<DividerLayer>,
): DividerLayer {
  return {
    ...layerBaseDefaults('Divider'),
    id: generateId('layer'),
    type: 'divider',
    borderColor: '#cccccc',
    borderStyle: 'solid',
    borderWidth: 1,
    dividerWidth: '100%',
    align: 'center',
    ...overrides,
  };
}

export function createSpacerLayer(
  overrides?: Partial<SpacerLayer>,
): SpacerLayer {
  return {
    ...layerBaseDefaults('Spacer'),
    id: generateId('layer'),
    type: 'spacer',
    height: 20,
    padding: defaultSpacing(0),
    ...overrides,
  };
}

// Starter markup for a fresh HTML block. Uses inline styles (the only reliable
// approach for email) to demonstrate what an HTML block can do — a styled
// container, heading, paragraph and a button — so the block renders something
// useful on the canvas and gives editors a template to build from.
export const DEFAULT_HTML_BLOCK_CONTENT = `<div style="padding:24px;background:#f0f6ff;border:1px solid #d0e1ff;border-radius:8px;font-family:Arial, Helvetica, sans-serif;text-align:center;">
  <h2 style="margin:0 0 8px;color:#1a73e8;font-size:20px;">Add your HTML here</h2>
  <p style="margin:0 0 16px;color:#444444;font-size:14px;line-height:1.6;">
    Double-click to edit this block. Use inline styles for reliable rendering across email clients — headings, colors, spacing, links and buttons all work.
  </p>
  <a href="#" style="display:inline-block;padding:10px 20px;background:#1a73e8;color:#ffffff;text-decoration:none;border-radius:6px;font-size:14px;font-weight:bold;">Example Button</a>
</div>`;

export function createHtmlLayer(overrides?: Partial<HtmlLayer>): HtmlLayer {
  return {
    ...layerBaseDefaults('Custom HTML'),
    id: generateId('layer'),
    type: 'html',
    content: DEFAULT_HTML_BLOCK_CONTENT,
    ...overrides,
  };
}

export function defaultColumn(): Column {
  return {
    id: generateId('col'),
    bgColor: '',
    padding: defaultSpacing(0),
    border: defaultBorderSides(),
    valign: 'middle',
    elements: [],
  };
}

export function createColumnsLayer(
  overrides?: Partial<ColumnsLayer>,
): ColumnsLayer {
  const layout = overrides?.layout ?? '2';
  const colCount = LAYOUT_COLUMN_COUNTS[layout] ?? 2;
  return {
    ...layerBaseDefaults('Columns'),
    id: generateId('layer'),
    type: 'columns',
    layout,
    columnsGap: 0,
    stackOnMobile: true,
    padding: defaultSpacing(0),
    columns:
      overrides?.columns ?? Array.from({ length: colCount }, () => defaultColumn()),
    ...overrides,
  };
}

/**
 * Default merge variable for each social platform, mapped to the facility's
 * saved social links. Social blocks pre-fill these so the icons resolve to the
 * facility's real URLs at send time.
 */
export const SOCIAL_PLATFORM_VARIABLES: Record<SocialLink['platform'], string> = {
  facebook: '{{facility_facebook}}',
  instagram: '{{facility_instagram}}',
  x: '{{facility_twitter}}',
  linkedin: '{{facility_linkedin}}',
  youtube: '{{facility_youtube}}',
  tiktok: '{{facility_tiktok}}',
  pinterest: '{{facility_pinterest}}',
  website: '{{facility_website}}',
  email: '{{facility_email}}',
};

/**
 * Maps each social platform to its column name in a social logo set
 * (the `social_logo_sets` table / SocialLogoSet API shape).
 */
export const SOCIAL_PLATFORM_LOGO_KEYS: Record<SocialPlatform, string> = {
  facebook: 'facebookLogo',
  instagram: 'instagramLogo',
  x: 'xLogo',
  linkedin: 'linkedinLogo',
  youtube: 'youtubeLogo',
  tiktok: 'tiktokLogo',
  pinterest: 'pinterestLogo',
  website: 'websiteLogo',
  email: 'emailLogo',
};

/**
 * Build a platform→URL map from a social logo set row. Only platforms with a
 * non-empty URL are included, so callers can treat a missing key as "fall back
 * to the built-in glyph". Accepts a loose record to avoid coupling defaults to
 * the service type.
 */
export function logosFromSet(set: object | null | undefined): SocialLogos {
  if (!set) return {};
  const row = set as Record<string, unknown>;
  const logos: SocialLogos = {};
  (Object.keys(SOCIAL_PLATFORM_LOGO_KEYS) as SocialPlatform[]).forEach((platform) => {
    const url = row[SOCIAL_PLATFORM_LOGO_KEYS[platform]];
    if (typeof url === 'string' && url.trim()) logos[platform] = url;
  });
  return logos;
}

function defaultSocialLinks(): SocialLink[] {
  const enabledByDefault: SocialLink['platform'][] = ['facebook', 'instagram', 'x'];
  return (Object.keys(SOCIAL_PLATFORM_VARIABLES) as SocialLink['platform'][]).map(
    (platform) => ({
      platform,
      url: SOCIAL_PLATFORM_VARIABLES[platform],
      enabled: enabledByDefault.includes(platform),
    }),
  );
}

export function createSocialLayer(
  overrides?: Partial<SocialLayer>,
): SocialLayer {
  return {
    ...layerBaseDefaults('Social Links'),
    id: generateId('layer'),
    type: 'social',
    style: 'icons-only',
    iconSize: 32,
    align: 'center',
    spacing: 8,
    lineHeight: '',
    iconColor: '',
    links: defaultSocialLinks(),
    ...overrides,
  };
}

export function createVideoLayer(
  overrides?: Partial<VideoLayer>,
): VideoLayer {
  return {
    ...layerBaseDefaults('Video'),
    id: generateId('layer'),
    type: 'video',
    thumbnailSrc: '',
    videoUrl: '',
    playButtonColor: '#ffffff',
    alt: 'Play video',
    ...overrides,
  };
}

export function createCountdownLayer(
  overrides?: Partial<CountdownLayer>,
): CountdownLayer {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 7);
  return {
    ...layerBaseDefaults('Countdown'),
    id: generateId('layer'),
    type: 'countdown',
    targetDate: tomorrow.toISOString(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    countdownStyle: 'light',
    labelColor: '#666666',
    digitColor: '#333333',
    digitBgColor: '#f0f0f0',
    showLabels: true,
    labels: { days: 'Days', hours: 'Hours', minutes: 'Minutes', seconds: 'Seconds' },
    ...overrides,
  };
}

// ---- Element factory functions --------------------------------------------

function elementBaseDefaults(): Omit<import('./dnd-model').ElementBase, 'id'> {
  return {
    padding: defaultSpacing(5),
    border: defaultBorderSides(),
    margin: defaultSpacing(0),
    hideOnMobile: false,
    hideOnDesktop: false,
    locked: true,
  };
}

export function createTextElement(
  overrides?: Partial<TextElement>,
): TextElement {
  return {
    ...elementBaseDefaults(),
    id: generateId('el'),
    type: 'text',
    content: '<p>Enter your text here</p>',
    textAlign: 'left',
    lineHeight: '',
    ...overrides,
  };
}

export function createImageElement(
  overrides?: Partial<ImageElement>,
): ImageElement {
  return {
    ...elementBaseDefaults(),
    id: generateId('el'),
    type: 'image',
    padding: defaultSpacing(0),
    src: '',
    alt: '',
    width: 300,
    autoWidth: true,
    href: '',
    target: '_blank',
    align: 'center',
    borderRadius: 0,
    retina: false,
    ...overrides,
  };
}

export function createButtonElement(
  overrides?: Partial<ButtonElement>,
): ButtonElement {
  return {
    ...elementBaseDefaults(),
    id: generateId('el'),
    type: 'button',
    text: 'Click Here',
    href: '#',
    target: '_blank',
    bgColor: '#1a73e8',
    textColor: '#ffffff',
    fontFamily: 'Arial, Helvetica, sans-serif',
    fontSize: '16px',
    fontWeight: 'bold',
    borderRadius: 4,
    buttonPadding: { top: 12, right: 24, bottom: 12, left: 24 },
    fullWidth: false,
    align: 'center',
    ...overrides,
  };
}

export function createDividerElement(
  overrides?: Partial<DividerElement>,
): DividerElement {
  return {
    ...elementBaseDefaults(),
    id: generateId('el'),
    type: 'divider',
    borderColor: '#cccccc',
    borderStyle: 'solid',
    borderWidth: 1,
    dividerWidth: '100%',
    ...overrides,
  };
}

export function createSpacerElement(
  overrides?: Partial<SpacerElement>,
): SpacerElement {
  return {
    ...elementBaseDefaults(),
    id: generateId('el'),
    type: 'spacer',
    height: 20,
    padding: defaultSpacing(0),
    ...overrides,
  };
}

export function createHtmlElement(
  overrides?: Partial<HtmlElement>,
): HtmlElement {
  return {
    ...elementBaseDefaults(),
    id: generateId('el'),
    type: 'html',
    content: DEFAULT_HTML_BLOCK_CONTENT,
    ...overrides,
  };
}

export function createSocialElement(
  overrides?: Partial<SocialElement>,
): SocialElement {
  return {
    ...elementBaseDefaults(),
    id: generateId('el'),
    type: 'social',
    style: 'icons-only',
    iconSize: 32,
    align: 'center',
    spacing: 8,
    lineHeight: '',
    iconColor: '',
    links: defaultSocialLinks(),
    ...overrides,
  };
}

// ---- Generic factory (by type string) -------------------------------------

export function createLayerByType(type: LayerType): Layer {
  const factories: Record<LayerType, () => Layer> = {
    text: createTextLayer,
    image: createImageLayer,
    button: createButtonLayer,
    divider: createDividerLayer,
    spacer: createSpacerLayer,
    html: createHtmlLayer,
    columns: createColumnsLayer,
    social: createSocialLayer,
    video: createVideoLayer,
    countdown: createCountdownLayer,
  };
  return factories[type]();
}

export function createElementByType(type: ElementType): Element {
  const factories: Record<ElementType, () => Element> = {
    text: createTextElement,
    image: createImageElement,
    button: createButtonElement,
    divider: createDividerElement,
    spacer: createSpacerElement,
    html: createHtmlElement,
    social: createSocialElement,
  };
  return factories[type]();
}

// ---- Layer → Element conversion -------------------------------------------

const ELEMENT_TYPES: Set<string> = new Set(['text', 'image', 'button', 'divider', 'spacer', 'html', 'social']);

export function convertLayerToElement(layer: Layer): Element | null {
  if (!ELEMENT_TYPES.has(layer.type)) return null;

  // Carry the box styling across so a bordered block keeps its frame when
  // dragged into a column. (Padding intentionally resets to the element
  // default, as it always has.)
  const base = {
    ...createElementByType(layer.type as ElementType),
    border: layer.border ?? defaultBorderSides(),
    margin: layer.margin ?? defaultSpacing(0),
  };

  switch (layer.type) {
    case 'text': return { ...base, content: (layer as TextLayer).content, textAlign: (layer as TextLayer).textAlign, lineHeight: (layer as TextLayer).lineHeight } as TextElement;
    case 'image': return { ...base, src: (layer as ImageLayer).src, alt: (layer as ImageLayer).alt, width: (layer as ImageLayer).width, autoWidth: (layer as ImageLayer).autoWidth, href: (layer as ImageLayer).href, target: (layer as ImageLayer).target, align: (layer as ImageLayer).align, borderRadius: (layer as ImageLayer).borderRadius, retina: (layer as ImageLayer).retina } as ImageElement;
    case 'button': return { ...base, text: (layer as ButtonLayer).text, href: (layer as ButtonLayer).href, target: (layer as ButtonLayer).target, bgColor: (layer as ButtonLayer).buttonBgColor, textColor: (layer as ButtonLayer).textColor, fontFamily: (layer as ButtonLayer).fontFamily, fontSize: (layer as ButtonLayer).fontSize, fontWeight: (layer as ButtonLayer).fontWeight, borderRadius: (layer as ButtonLayer).borderRadius, buttonPadding: (layer as ButtonLayer).buttonPadding, fullWidth: (layer as ButtonLayer).fullWidth } as ButtonElement;
    case 'divider': return { ...base, borderColor: (layer as DividerLayer).borderColor, borderStyle: (layer as DividerLayer).borderStyle, borderWidth: (layer as DividerLayer).borderWidth, dividerWidth: (layer as DividerLayer).dividerWidth } as DividerElement;
    case 'spacer': return { ...base, height: (layer as SpacerLayer).height } as SpacerElement;
    case 'html': return { ...base, content: (layer as HtmlLayer).content } as HtmlElement;
    case 'social': return { ...base, style: (layer as SocialLayer).style, iconSize: (layer as SocialLayer).iconSize, align: (layer as SocialLayer).align, spacing: (layer as SocialLayer).spacing, lineHeight: (layer as SocialLayer).lineHeight, iconColor: (layer as SocialLayer).iconColor, links: (layer as SocialLayer).links, logoSetId: (layer as SocialLayer).logoSetId, logos: (layer as SocialLayer).logos } as SocialElement;
    default: return null;
  }
}

// ---- Element → Layer conversion (drag a block out of a column) ------------

export function convertElementToLayer(el: Element): Layer | null {
  if (!ELEMENT_TYPES.has(el.type)) return null;

  // Mirror of convertLayerToElement: the frame travels with the block.
  const base = {
    ...createLayerByType(el.type as LayerType),
    border: el.border ?? defaultBorderSides(),
    margin: el.margin ?? defaultSpacing(0),
  };

  switch (el.type) {
    case 'text': return { ...base, content: (el as TextElement).content, textAlign: (el as TextElement).textAlign, lineHeight: (el as TextElement).lineHeight } as TextLayer;
    case 'image': return { ...base, src: (el as ImageElement).src, alt: (el as ImageElement).alt, width: (el as ImageElement).width, autoWidth: (el as ImageElement).autoWidth, href: (el as ImageElement).href, target: (el as ImageElement).target, align: (el as ImageElement).align, borderRadius: (el as ImageElement).borderRadius, retina: (el as ImageElement).retina } as ImageLayer;
    case 'button': return { ...base, text: (el as ButtonElement).text, href: (el as ButtonElement).href, target: (el as ButtonElement).target, buttonBgColor: (el as ButtonElement).bgColor, textColor: (el as ButtonElement).textColor, fontFamily: (el as ButtonElement).fontFamily, fontSize: (el as ButtonElement).fontSize, fontWeight: (el as ButtonElement).fontWeight, borderRadius: (el as ButtonElement).borderRadius, buttonPadding: (el as ButtonElement).buttonPadding, fullWidth: (el as ButtonElement).fullWidth, align: (el as ButtonElement).align } as ButtonLayer;
    case 'divider': return { ...base, borderColor: (el as DividerElement).borderColor, borderStyle: (el as DividerElement).borderStyle, borderWidth: (el as DividerElement).borderWidth, dividerWidth: (el as DividerElement).dividerWidth } as DividerLayer;
    case 'spacer': return { ...base, height: (el as SpacerElement).height } as SpacerLayer;
    case 'html': return { ...base, content: (el as HtmlElement).content } as HtmlLayer;
    case 'social': return { ...base, style: (el as SocialElement).style, iconSize: (el as SocialElement).iconSize, align: (el as SocialElement).align, spacing: (el as SocialElement).spacing, lineHeight: (el as SocialElement).lineHeight, iconColor: (el as SocialElement).iconColor, links: (el as SocialElement).links, logoSetId: (el as SocialElement).logoSetId, logos: (el as SocialElement).logos } as SocialLayer;
    default: return null;
  }
}

// ---- Empty model ----------------------------------------------------------

export function createEmptyModel(): DndEmailModel {
  return {
    editorVersion: CURRENT_EDITOR_VERSION,
    globalStyles: defaultGlobalStyles(),
    wrapper: defaultWrapperSettings(),
    preheaderText: '',
    layers: [],
  };
}
